import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type ModalKey =
  | 'persona-switcher'
  | 'studio-switcher'
  | 'self-book-generator'
  | 'cancellation'
  | 'booking-modal'
  | 'rebooking-options'
  | 'deposit-link-modal'
  | 'rapport-note';

export interface NotificationToast {
  id: string;
  intensity: 'info' | 'high' | 'critical';
  title: string;
  body?: string;
  ttlMs: number;
  createdAt: string;
  studioContext?: string;
  customerId?: string;
}

export interface IncomingCallNotification {
  id: string;
  customerId: string;
  studioId: string;
  startedAt: string;
  status: 'ringing' | 'answered' | 'declined' | 'overflow';
}

/**
 * Which arrangement the queue work modal uses. `classic` is the built
 * three-pane layout; `focus` is the conversation-first two-pane layout that
 * folds the brief and availability behind one on-demand panel. Persisted so a
 * chosen layout survives the modal remounting per queue item.
 */
export type QueueModalLayout = 'classic' | 'focus';

export interface UIState {
  controlPanelOpen: boolean;
  activityFeedOpen: boolean;
  railCollapsed: boolean;
  queueModalLayout: QueueModalLayout;
  modals: Record<ModalKey, boolean>;
  modalContext: Record<string, unknown>;
  notifications: NotificationToast[];
  incomingCalls: IncomingCallNotification[];
  muted: boolean;
  narrationOn: boolean;
  toggleControlPanel: () => void;
  toggleActivityFeed: () => void;
  toggleRail: () => void;
  setModal: (key: ModalKey, open: boolean, context?: Record<string, unknown>) => void;
  pushNotification: (n: Omit<NotificationToast, 'id' | 'createdAt'>) => void;
  dismissNotification: (id: string) => void;
  addIncomingCall: (call: Omit<IncomingCallNotification, 'status'>) => void;
  setIncomingCallStatus: (id: string, status: IncomingCallNotification['status']) => void;
  clearIncomingCalls: () => void;
  setMuted: (muted: boolean) => void;
  setNarration: (on: boolean) => void;
  setQueueModalLayout: (layout: QueueModalLayout) => void;
}

let _toastSeed = 0;
const nextId = (prefix: string) => `${prefix}-${Date.now()}-${++_toastSeed}`;

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
  controlPanelOpen: false,
  // Off by default. It is a permanent right-hand column, and every reload used to
  // reopen it because nothing persisted the operator's choice.
  activityFeedOpen: false,
  railCollapsed: false,
  // Classic three-pane by default; the focus layout is opt-in per session.
  queueModalLayout: 'classic',
  modals: {
    'persona-switcher': false,
    'studio-switcher': false,
    'self-book-generator': false,
    cancellation: false,
    'booking-modal': false,
    'rebooking-options': false,
    'deposit-link-modal': false,
    'rapport-note': false,
  },
  modalContext: {},
  notifications: [],
  incomingCalls: [],
  muted: false,
  narrationOn: false,
  toggleControlPanel: () => set((s) => ({ controlPanelOpen: !s.controlPanelOpen })),
  toggleActivityFeed: () => set((s) => ({ activityFeedOpen: !s.activityFeedOpen })),
  toggleRail: () => set((s) => ({ railCollapsed: !s.railCollapsed })),
  setModal: (key, open, context) =>
    set((s) => ({
      modals: { ...s.modals, [key]: open },
      modalContext: open ? { ...s.modalContext, [key]: context ?? {} } : s.modalContext,
    })),
  pushNotification: (n) =>
    set((s) => ({
      notifications: [
        ...s.notifications,
        { ...n, id: nextId('toast'), createdAt: new Date().toISOString() },
      ],
    })),
  dismissNotification: (id) =>
    set((s) => ({ notifications: s.notifications.filter((n) => n.id !== id) })),
  addIncomingCall: (call) =>
    set((s) => ({ incomingCalls: [...s.incomingCalls, { ...call, status: 'ringing' }] })),
  setIncomingCallStatus: (id, status) =>
    set((s) => ({
      incomingCalls: s.incomingCalls.map((c) => (c.id === id ? { ...c, status } : c)),
    })),
  clearIncomingCalls: () => set({ incomingCalls: [] }),
  setMuted: (muted) => set({ muted }),
  setNarration: (on) => set({ narrationOn: on }),
  setQueueModalLayout: (queueModalLayout) => set({ queueModalLayout }),
    }),
    {
      name: 'ati-ui',
      storage: createJSONStorage(() => sessionStorage),
      // Chrome preferences only. Modals, notifications and incoming calls are
      // live session state and must never be restored from a previous load.
      partialize: (s) => ({
        activityFeedOpen: s.activityFeedOpen,
        railCollapsed: s.railCollapsed,
        queueModalLayout: s.queueModalLayout,
      }),
    },
  ),
);
